import { createLogger } from '@/lib/logger';
import { CartService } from './cart-service';
import { Cart, CartEvents, CartLine } from './core';
import { CartEventBus } from './event-bus';

/**
 * Lightweight in-memory cart store that mirrors the latest cart.
 *
 * - Initializes by fetching the cart once via `CartService.get()`.
 * - Subscribes to success lifecycle events to keep its state in sync.
 * - Provides simple getters for current cart and line items.
 */
export class CartStore {
  private cart: Cart | null = null;
  private cartService: CartService;
  private eventBus: CartEventBus;
  private logger = createLogger('cart-store');

  /**
   * Get the last known cart, or `null` if not yet loaded.
   */
  getCart(): Cart | null {
    return this.cart;
  }

  /**
   * Get the last known cart line items, or an empty array when no cart is present.
   */
  getLineItems(): CartLine[] {
    return this.cart?.items ?? [];
  }

  /**
   * Replace or set the current cart snapshot.
   */
  private upsertCart(cart: Cart): void {
    this.cart = cart;
  }

  /**
   * Create a CartStore bound to an event bus and service.
   * @param bus Event bus to listen for lifecycle events.
   * @param cartService Service used for initial load and potential helpers.
   */
  constructor(bus: CartEventBus, cartService: CartService) {
    this.eventBus = bus;
    this.cartService = cartService;

    // Make sure the cart is loaded
    this.cartService.get().then((cart) => {
      this.logger.info('Cart loaded', cart);
      this.upsertCart(cart);
    });

    this.eventBus.on(CartEvents.ChangeSuccess, (e: CustomEvent) => {
      this.logger.info('Cart changed', e.detail.cart);
      this.upsertCart(e.detail.cart);
    });

    this.eventBus.on(CartEvents.ClearSuccess, (e: CustomEvent) => {
      this.logger.info('Cart cleared', e.detail.cart);
      this.upsertCart(e.detail.cart);
    });

    this.eventBus.on(CartEvents.AddSuccess, (e: CustomEvent) => {
      this.logger.info('Item added', e.detail.cart);
      this.upsertCart(e.detail.cart);
    });

    this.eventBus.on(CartEvents.UpdateSuccess, (e: CustomEvent) => {
      this.logger.info('Cart updated', e.detail.cart);
      this.upsertCart(e.detail.cart);
    });
  }
}
